/**
 * Currency Formatting Utilities
 *
 * Centralized currency formatting and metadata for multi-currency support.
 * Uses Intl.NumberFormat for proper locale-aware formatting.
 */

export interface CurrencyConfig {
  symbol: string;
  name: string;
  locale: string;
  defaultMaxBudget: number;
}

/**
 * Supported currencies with metadata.
 * Keep in sync with core/backend/currency.py CURRENCY_CONFIG.
 */
export const CURRENCY_CONFIG: Record<string, CurrencyConfig> = {
  USD: { symbol: '$', name: 'US Dollar', locale: 'en-US', defaultMaxBudget: 500 },
  INR: { symbol: '₹', name: 'Indian Rupee', locale: 'en-IN', defaultMaxBudget: 20000 },
  EUR: { symbol: '€', name: 'Euro', locale: 'de-DE', defaultMaxBudget: 450 },
  GBP: { symbol: '£', name: 'British Pound', locale: 'en-GB', defaultMaxBudget: 400 },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', locale: 'ar-AE', defaultMaxBudget: 1800 },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', locale: 'en-CA', defaultMaxBudget: 650 },
  AUD: { symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU', defaultMaxBudget: 750 },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', locale: 'en-SG', defaultMaxBudget: 650 },
  JPY: { symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP', defaultMaxBudget: 50000 },
};

export const SUPPORTED_CURRENCIES = Object.keys(CURRENCY_CONFIG);

/**
 * Format a numeric amount with the correct currency symbol and locale.
 *
 * @example
 * formatCurrency(1999, 'INR')  // "₹1,999.00"
 * formatCurrency(99, 'USD')     // "$99.00"
 * formatCurrency(50000, 'JPY')  // "¥50,000"
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD;
  try {
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: currency === 'JPY' ? 0 : 0,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
    }).format(amount);
  } catch {
    // Fallback if Intl doesn't recognize the currency
    return `${config.symbol}${amount.toLocaleString()}`;
  }
}

/**
 * Get the currency symbol for a currency code.
 */
export function getCurrencySymbol(currency: string): string {
  return CURRENCY_CONFIG[currency]?.symbol || '$';
}

/**
 * Get the default max campaign budget for a currency.
 */
export function getDefaultMaxBudget(currency: string): number {
  return CURRENCY_CONFIG[currency]?.defaultMaxBudget || CURRENCY_CONFIG.USD.defaultMaxBudget;
}

/**
 * Get display label for currency dropdown (e.g., "USD ($)")
 */
export function getCurrencyLabel(currency: string): string {
  const config = CURRENCY_CONFIG[currency];
  if (!config) return currency;
  return `${currency} (${config.symbol})`;
}

/**
 * Get all currencies as options for a dropdown.
 */
export function getCurrencyOptions(): { value: string; label: string; description: string }[] {
  return SUPPORTED_CURRENCIES.map((code) => ({
    value: code,
    label: getCurrencyLabel(code),
    description: CURRENCY_CONFIG[code].name,
  }));
}
